<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <!-- Header -->
      <div class="page-header">
        <div class="row">
          <div
            class="col-sm-12 d-flex align-items-center justify-content-between"
          >
            <div>
              <h3 class="page-title mb-1">
                {{
                  isEditMode
                    ? "Sửa nội quy hoặc hướng dẫn"
                    : "Thêm nội quy hoặc hướng dẫn mới"
                }}
              </h3>
              <ul class="breadcrumb mb-0">
                <li class="breadcrumb-item">
                  <router-link to="/">Dashboard</router-link>
                </li>
                <li class="breadcrumb-item active">
                  {{
                    isEditMode
                      ? "Sửa nội quy hoặc hướng dẫn"
                      : "Thêm nội quy hoặc hướng dẫn mới "
                  }}(Admin)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <FileManagerModal
        v-if="showFileManagerModal"
        :folderTabs="imageTabs"
        @close="showFileManagerModal = false"
        @file-selected="onFileSelected"
        @tab-created="onTabCreated"
        @tab-deleted="onTabDeleted"
      />

      <div class="card">
        <div
          class="card-header d-flex justify-content-between align-items-center"
        >
          <h5 class="mb-0"></h5>
          <router-link to="/noi-quy" class="btn btn-secondary btn-sm">
            <i class="la la-arrow-left mr-1"></i> Quay lại
          </router-link>
        </div>
        <div class="card-body">
          <div v-if="loading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
          <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

          <form v-else @submit.prevent="handleSubmit">
            <div class="form-row">
              <div class="form-group col-md-3">
                <label for="ten">Tên <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="ten"
                  v-model.trim="form.ten"
                  required
                />
              </div>
              <div class="form-group col-md-3">
                <label for="loai">Loại</label>
                <select id="loai" class="form-control" v-model="form.loai">
                  <option value="NOIQUY">Nội quy công ty</option>
                  <option value="QUYDINH">Quy định</option>
                  <option value="HUONGDAN">Hướng dẫn</option>
                </select>
              </div>
              <div class="form-group col-md-3">
                <label for="ngayHieuLuc">Ngày hiệu lực</label>
                <input
                  type="date"
                  class="form-control"
                  id="ngayHieuLuc"
                  v-model="form.ngayHieuLuc"
                />
              </div>
              <div class="form-group col-md-3">
                <label for="phongBanId">Phòng ban</label>
                <select
                  id="phongBanId"
                  class="form-control"
                  v-model="form.phongBanId"
                >
                  <option :value="null">Toàn công ty</option>
                  <option
                    v-for="pb in phongBanOptions"
                    :key="pb.idPhongBan"
                    :value="pb.idPhongBan"
                  >
                    {{ pb.tenPhongBan }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label>Nội dung <span class="text-danger">*</span></label>
              <Editor
                v-model="form.noiDung"
                api-key="i4a8vcdem3s7a77hhng1uc8pni7sr18swvn73zohd5gla8bq"
                :init="tinyInit"
              />
            </div>

            <hr />

            <div class="text-right">
              <router-link to="/noi-quy" class="btn btn-secondary mr-2"
                >Hủy</router-link
              >
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="!canSubmit || submitting"
              >
                <span
                  v-if="submitting"
                  class="spinner-border spinner-border-sm mr-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                {{ submitting ? "Đang lưu..." : "Lưu lại" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import Editor from "@tinymce/tinymce-vue";
import api from "@/api";
import { NoiQuyApi } from "@/services/noiQuyService";
import FileManagerModal from "@/components/FileManagerModal.vue";
import phongBanService from "@/services/phongBanService";
import fileService from "@/services/fileService";

// --- Core Vue & Router ---
const route = useRoute();
const router = useRouter();
const toast = useToast();

const noiQuyId = ref(route.params.id || null);
const isEditMode = computed(() => !!noiQuyId.value);

// --- Form State ---
const form = ref({
  id: null,
  ten: "",
  noiDung: "",
  loai: "NOIQUY",
  ngayHieuLuc: null,
  phongBanId: null,
});
const loading = ref(false);
const submitting = ref(false);
const error = ref(null);

const phongBanOptions = ref([]);
const loadingPhongBan = ref(false);

const getFolderName = () => {
  switch (form.value.loai) {
    case "HUONGDAN":
      return "huong-dan";
    case "QUYDINH":
      return "quy-dinh";
    case "NOIQUY":
    default:
      return "noi-quy";
  }
};

// HÀM TẢI PHÒNG BAN
const loadPhongBanOptions = async () => {
  loadingPhongBan.value = true;
  try {
    const data = await phongBanService.getActiveOptions();
    phongBanOptions.value = data.data.data || [];
  } catch (e) {
    toast.error("Không thể tải danh sách phòng ban.");
    phongBanOptions.value = [];
  } finally {
    loadingPhongBan.value = false;
  }
};

// --- File Manager Modal State (Lấy từ code mẫu của bạn) ---
const showFileManagerModal = ref(false);
const fileManagerCallback = ref(null);
const imageTabs = ref([]);
const targetField = ref(null); // Không dùng 'avatar' hay 'content' mà chung chung
// Thực ra chỉ cần 'content' cho TinyMCE

const fetchFolders = async () => {
  try {
    const res = await api.get("/api/images/folders"); // Lấy từ code mẫu
    let folderNames = res.data || [];
    const targetFolder = getFolderName();

    if (!folderNames.includes(targetFolder)) {
      folderNames.push(targetFolder);
    }

    folderNames.sort((a, b) => {
      if (a === targetFolder) return -1; // a lên trước
      if (b === targetFolder) return 1; // b lên trước
      return a.localeCompare(b); // Các folder khác xếp alphabe
    });

    imageTabs.value = folderNames.map((folderName, index) => ({
      id: folderName, // Dùng tên làm ID để tránh lỗi khi sort lại
      name: folderName,
      folderName: folderName,
    }));
  } catch {
    imageTabs.value = [];
    toast.error("Lỗi khi tải danh sách folder.");
  }
};

const onTabCreated = (newTab) => {
  imageTabs.value.push(newTab);
};
const onTabDeleted = (tabIdToDelete) => {
  imageTabs.value = imageTabs.value.filter((tab) => tab.id !== tabIdToDelete);
};

const onFileSelected = async (image) => {
  if (!image || !image.url) {
    toast.error("Không thể chọn ảnh, URL không hợp lệ.");
    return;
  }
  if (targetField.value === "content" && fileManagerCallback.value) {
    fileManagerCallback.value(image.url, {
      alt: image.title,
      title: image.title,
    });
  }
  showFileManagerModal.value = false;
  fileManagerCallback.value = null;
  targetField.value = null;
};

// --- TinyMCE Config (Lấy từ code mẫu của bạn) ---
const tinyInit = {
  height: 500,
  menubar: true,
  plugins: [
    "advlist",
    "autolink",
    "link",
    "image",
    "lists",
    "charmap",
    "preview",
    "anchor",
    "searchreplace",
    "visualblocks",
    "code",
    "fullscreen",
    "insertdatetime",
    "media",
    "table",
    "paste",
    "help",
    "wordcount",
  ],
  toolbar: [
    "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough forecolor backcolor",
    "alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media",
    "removeformat | table | code fullscreen | preview | help",
  ],
  relative_urls: false,
  remove_script_host: false,
  document_base_url: window.location.origin + "/",
  toolbar_mode: "floating",
  content_style:
    "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",

  // 1. XỬ LÝ KÉO THẢ / PASTE ẢNH (Auto Upload)
  images_upload_handler: (blobInfo, progress) =>
    new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append("files", blobInfo.blob(), blobInfo.filename());
        formData.append("folderName", getFolderName()); // Lấy folder động

        const response = await fileService.upload(formData);

        if (response.data && response.data.length > 0) {
          resolve(response.data[0].location); // Trả về URL
        } else {
          reject("Upload thất bại.");
        }
      } catch (error) {
        reject("Lỗi upload: " + (error.message || error));
      }
    }),

  file_picker_callback: async (cb, value, meta) => {
    if (meta.filetype === "image") {
      targetField.value = "content";
      fileManagerCallback.value = cb;
      await fetchFolders();
      showFileManagerModal.value = true;
    }
  },
};

// --- Computed & Methods ---
const canSubmit = computed(() => {
  return form.value.ten?.trim() && form.value.noiDung?.trim();
});

const loadNoiQuy = async () => {
  if (!isEditMode.value) return;
  loading.value = true;
  error.value = null;
  try {
    const response = await NoiQuyApi.getById(noiQuyId.value);
    const data = response.data;
    form.value = {
      ...data,
      // Đảm bảo ngày tháng được format đúng cho input[type=date]
      ngayHieuLuc: data.ngayHieuLuc ? data.ngayHieuLuc.split("T")[0] : null,
    };
  } catch (e) {
    error.value = "Không thể tải dữ liệu nội quy.";
    toast.error(error.value);
  } finally {
    loading.value = false;
  }
};

const handleSubmit = async () => {
  if (!canSubmit.value) {
    toast.error("Vui lòng nhập Tên nội quy và Nội dung.");
    return;
  }
  submitting.value = true;
  try {
    // Chuẩn bị payload DTO
    const payload = {
      ten: form.value.ten,
      noiDung: form.value.noiDung,
      loai: form.value.loai,
      ngayHieuLuc: form.value.ngayHieuLuc || null,
      phongBanId: form.value.phongBanId || null,
    };

    if (isEditMode.value) {
      await NoiQuyApi.update(noiQuyId.value, payload);
      toast.success("Cập nhật nội quy thành công!");
    } else {
      await NoiQuyApi.create(payload);
      toast.success("Thêm mới nội quy thành công!");
    }
    router.push("/noi-quy");
  } catch (e) {
    const errorMsg =
      e?.response?.data?.message || e?.message || "Đã xảy ra lỗi";
    toast.error(`Lỗi khi lưu: ${errorMsg}`);
  } finally {
    submitting.value = false;
  }
};

// --- Lifecycle Hook ---
onMounted(() => {
  if (isEditMode.value) {
    loadNoiQuy();
  } else {
    // Set ngày hiệu lực mặc định là hôm nay khi tạo mới
    form.value.ngayHieuLuc = new Date().toISOString().split("T")[0];
  }
  loadPhongBanOptions();
});
</script>
